import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { ImageWrapper } from 'components/EquipmentImage/ImageWrapper';
import { Equipment } from 'hooks/useEquipment/types';

interface EquipmentImageProps {
  equipmentId: number | string;
  equipment: Equipment[];
}

export const EquipmentImage = ({ equipmentId, equipment }: EquipmentImageProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  // Helper function to find equipment by ID and get image URL
  const getEquipmentImageUrl = (equipmentId: number | string): string | null => {
    const foundEquipment = equipment.find((item) => item.equipmentId.toString() === equipmentId.toString());
    return foundEquipment?.imageUrl || foundEquipment?.image?.url || null;
  };

  const imageUrl = getEquipmentImageUrl(equipmentId);

  // Get equipment name for alt text
  const foundEquipment = equipment.find((item) => item.equipmentId.toString() === equipmentId.toString());
  const equipmentName = foundEquipment?.name || 'Equipment';

  const shouldShowImage = imageUrl && !imageError;
  const shouldShowPlaceholder = !imageUrl || imageError;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '162px',
        height: '104px',
      }}
    >
      {shouldShowImage && (
        <ImageWrapper
          data-testid="equipment-image"
          src={imageUrl}
          alt={equipmentName}
          onError={() => setImageError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
      {shouldShowPlaceholder && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.grey[500],
            textAlign: 'center',
            p: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            {t('blueprints:wizard.fields.suggestedRentals.equipment.imageNotAvailable')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
